import React, { useEffect, useMemo, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useGetApCustomersByDateMutation,
  useGetApInvoicesMutation,
  useSendApEmailMutation,
  useGetApHistoryMutation,
  useGetEmailTemplateByNameQuery,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import { fmtCustomerName } from "../../Core/utils/nameUtils";
import { IApEmailLog, IApInvoice } from "../../Api/Interface/api.interface";
import dayjs from "dayjs";

// Icons
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AttachEmailOutlinedIcon from "@mui/icons-material/AttachEmailOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ClientGroup {
  customer: any;
  invoices: IApInvoice[];
  emailTo: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null | undefined) =>
  d ? dayjs(d).format("MMM D, YYYY") : "—";

const fmtAmt = (v: string | number | null | undefined) =>
  v != null ? `$${parseFloat(String(v)).toFixed(2)}` : "$0.00";

const deriveStatus = (amtPaid: number | null, grandTotal: string) => {
  const paid = Number(amtPaid ?? 0);
  const total = parseFloat(grandTotal ?? "0");
  if (total > 0 && paid >= total) return { label: "Paid", color: "#16a34a", bg: "#dcfce7" };
  if (paid > 0) return { label: "Partial", color: "#0284c7", bg: "#e0f2fe" };
  return { label: "Unpaid", color: "#dc2626", bg: "#fee2e2" };
};

// ── Filter Tabs ───────────────────────────────────────────────────────────────

const FilterTabs = ({
  filter,
  onChange,
  counts,
}: {
  filter: "all" | "pending" | "sent";
  onChange: (f: "all" | "pending" | "sent") => void;
  counts: { all: number; pending: number; sent: number };
}) => {
  const tabs: { value: "all" | "pending" | "sent"; label: string; activeColor: string }[] = [
    { value: "all",     label: `All (${counts.all})`,         activeColor: "#2f3891" },
    { value: "pending", label: `Pending (${counts.pending})`, activeColor: "#c2410c" },
    { value: "sent",    label: `Sent (${counts.sent})`,       activeColor: "#15803d" },
  ];
  return (
    <Box sx={{ display: "flex", bgcolor: "#f1f5f9", borderRadius: 2, p: 0.4, gap: 0.25, width: "fit-content" }}>
      {tabs.map(({ value, label, activeColor }) => {
        const active = filter === value;
        return (
          <Box
            key={value}
            onClick={() => onChange(value)}
            sx={{
              px: 1.75,
              py: 0.6,
              borderRadius: 1.5,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              bgcolor: active ? "#fff" : "transparent",
              color: active ? activeColor : "#64748b",
              boxShadow: active ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
              transition: "all 0.15s ease",
              userSelect: "none" as const,
              "&:hover": {
                color: activeColor,
                bgcolor: active ? "#fff" : "rgba(255,255,255,0.7)",
              },
            }}
          >
            {label}
          </Box>
        );
      })}
    </Box>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionCard = ({
  icon,
  title,
  subtitle,
  children,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 1.75,
        borderBottom: "1px solid #f1f5f9",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            background: "linear-gradient(135deg,#2f3891,#2f3891)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b", lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>{subtitle}</Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

const StatBadge = ({
  label,
  value,
  color = "#2f3891",
  onClick,
  active,
}: {
  label: string;
  value: string | number;
  color?: string;
  onClick?: () => void;
  active?: boolean;
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      bgcolor: active ? color : "#f8fafc",
      border: `1px solid ${active ? color : "#e2e8f0"}`,
      borderRadius: 2,
      px: 2,
      py: 1.25,
      minWidth: 90,
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.15s ease",
      ...(onClick && {
        "&:hover": {
          bgcolor: active ? color : `${color}15`,
          borderColor: color,
          transform: "translateY(-1px)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
      }),
    }}
  >
    <Typography sx={{ fontSize: 18, fontWeight: 800, color: active ? "#fff" : color, lineHeight: 1.2 }}>{value}</Typography>
    <Typography sx={{ fontSize: 10, color: active ? "rgba(255,255,255,0.8)" : "#94a3b8", fontWeight: 600, mt: 0.3, textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

// ── History Dialog ─────────────────────────────────────────────────────────────

function HistoryDialog({
  open,
  onClose,
  logs,
}: {
  open: boolean;
  onClose: () => void;
  logs: IApEmailLog[];
}) {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: "linear-gradient(135deg,#2f3891,#2f3891)",
          px: 3, py: 2,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HistoryIcon sx={{ color: "#fff", fontSize: 20 }} />
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15, lineHeight: 1 }}>
                AP Email History
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 11, mt: 0.3 }}>
                {logs.length} email{logs.length !== 1 ? "s" : ""} sent
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}
            sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {logs.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <EmailOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
            <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>No AP emails sent yet.</Typography>
          </Box>
        ) : (
          <Box>
            {logs.map((log, idx) => (
              <Box key={log.id} sx={{ borderBottom: idx < logs.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                {/* Row header */}
                <Box
                  onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 3, py: 2, cursor: "pointer",
                    "&:hover": { bgcolor: "#f8fafc" },
                    transition: "background 0.15s",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: 2,
                      background: "linear-gradient(135deg,#2f3891,#2f3891)",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <AttachEmailOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                        {log.subject}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.2 }}>
                        To: <strong>{log.sent_to}</strong>
                        {log.cc ? ` · CC: ${log.cc}` : ""}
                        {" · "}
                        {dayjs(log.created_at).format("MMM D, YYYY [at] h:mm A")}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#2f3891" }}>
                        {fmtAmt(log.total_amount)}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                        {log.invoice_ids?.length ?? 0} invoice{(log.invoice_ids?.length ?? 0) !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                    {expanded === log.id ? (
                      <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                    )}
                  </Box>
                </Box>

                {/* Expanded detail */}
                <Collapse in={expanded === log.id}>
                  <Box sx={{ px: 3, pb: 2.5, bgcolor: "#fafafe", borderTop: "1px solid #f1f5f9" }}>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                          Invoices Included
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                          {log.invoice_nos || "—"}
                        </Typography>
                      </Grid>
                      {log.trip_date && (
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                            Trip Date
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "#334155" }}>
                            {fmtDate(log.trip_date)}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.75 }}>
                          Message
                        </Typography>
                        <Box sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5 }}>
                          <Typography sx={{ fontSize: 12, color: "#475569", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                            {log.message}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const APEmailPage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const API_URL = process.env.REACT_APP_API_URL;

  // RTK mutations
  const [fetchApCustomersByDate, { isLoading: customersLoading }] = useGetApCustomersByDateMutation();
  const [fetchApInvoices, { isLoading: invoicesLoading }] = useGetApInvoicesMutation();
  const [sendApEmail, { isLoading: sendingEmail }] = useSendApEmailMutation();
  const [fetchApHistory, { isLoading: historyLoading }] = useGetApHistoryMutation();

  const { data: apTemplateData } = useGetEmailTemplateByNameQuery("AP Email");

  // ── Local state ──────────────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [invoices, setInvoices] = useState<IApInvoice[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);

  // Email compose
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  // History dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<IApEmailLog[]>([]);

  // Invoice filter
  const [invoiceFilter, setInvoiceFilter] = useState<"all" | "pending" | "sent">("all");

  // Multi-client mode
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);
  const [multiSelectedIds, setMultiSelectedIds] = useState<Record<number, number[]>>({});
  const [multiGroupsLoaded, setMultiGroupsLoaded] = useState(false);
  const [loadingAllClients, setLoadingAllClients] = useState(false);

  // ── Derived values ───────────────────────────────────────────────────────────
  const selectedInvoices = useMemo(
    () => invoices.filter((inv) => selectedIds.includes(inv.id)),
    [invoices, selectedIds]
  );

  const totalSelected = useMemo(
    () => selectedInvoices.reduce((sum, inv) => sum + parseFloat(inv.quoteWorkOrderSaleGrandTotal ?? "0"), 0),
    [selectedInvoices]
  );

  const displayedInvoices = useMemo(() => {
    if (invoiceFilter === "pending") return invoices.filter((i) => !i.ap_email_sent);
    if (invoiceFilter === "sent") return invoices.filter((i) => i.ap_email_sent);
    return invoices;
  }, [invoices, invoiceFilter]);

  const allChecked = displayedInvoices.length > 0 && displayedInvoices.every((i) => selectedIds.includes(i.id));
  const someChecked = displayedInvoices.some((i) => selectedIds.includes(i.id)) && !allChecked;

  // ── Load invoices for a given customer (used for auto-load + manual refresh) ──
  const loadInvoicesForCustomer = async (customer: any) => {
    if (!customer || !selectedDate) return;
    try {
      const res = await fetchApInvoices({
        customer_id: customer.id,
        date: selectedDate,
      }).unwrap();
      setInvoices(res.data ?? []);
      setSelectedIds([]);
      setInvoiceFilter("all");
      setInvoicesLoaded(true);
    } catch {
      showErrorMessage("Failed to load invoices.");
    }
  };

  // ── Load ALL clients' invoices in parallel (multi-client mode) ───────────────
  const loadAllClients = async (customerList: any[]) => {
    if (!selectedDate || customerList.length === 0) return;
    setLoadingAllClients(true);
    setSelectedCustomer(null);
    setInvoices([]);
    setSelectedIds([]);
    setInvoicesLoaded(false);

    const results = await Promise.all(
      customerList.map(async (customer) => {
        try {
          const res = await fetchApInvoices({ customer_id: customer.id, date: selectedDate }).unwrap();
          return {
            customer,
            invoices: (res.data ?? []) as IApInvoice[],
            emailTo: customer.customerCompanyEmail || customer.customerEmail || "",
          } as ClientGroup;
        } catch {
          return { customer, invoices: [], emailTo: "" } as ClientGroup;
        }
      })
    );

    // Only keep groups that have at least one invoice
    const nonEmpty = results.filter((g) => g.invoices.length > 0);
    setClientGroups(nonEmpty);
    setMultiSelectedIds(Object.fromEntries(nonEmpty.map((g) => [g.customer.id, []])));
    setMultiGroupsLoaded(true);
    setLoadingAllClients(false);

    // Pre-fill subject/message as template (placeholders stay in)
    const formattedDate = dayjs(selectedDate).format("MMM D, YYYY");
    const formattedDateLong = dayjs(selectedDate).format("MMMM D, YYYY");
    const tpl = (apTemplateData as any)?.data;
    if (tpl?.subject) {
      setEmailSubject(tpl.subject.replace(/\[customer_name\]/g, "[client_name]").replace(/\[trip_date\]/g, formattedDate));
    } else {
      setEmailSubject(`Charter Invoice(s) – [client_name] – ${formattedDate}`);
    }
    if (tpl?.message) {
      const stripped = tpl.message.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s{2,}/g, "\n").trim();
      setEmailMessage(stripped.replace(/\[customer_name\]/g, "[client_name]").replace(/\[trip_date\]/g, formattedDateLong));
    } else {
      setEmailMessage(`Please find below the invoice details for charter services provided on ${formattedDateLong}.\n\nPayment is due within 30 days of invoice date.\n\nThank you for your business.`);
    }
  };

  // ── Load customers whenever the date changes; auto-select if only 1 ──────────
  useEffect(() => {
    if (!selectedDate) return;
    setSelectedCustomer(null);
    setInvoices([]);
    setSelectedIds([]);
    setInvoicesLoaded(false);
    setClientGroups([]);
    setMultiSelectedIds({});
    setMultiGroupsLoaded(false);
    fetchApCustomersByDate({ date: selectedDate })
      .unwrap()
      .then((res) => {
        const list = (res as any).data ?? [];
        setCustomers(list);
        if (list.length === 1) {
          setSelectedCustomer(list[0]);
        }
      })
      .catch(() => setCustomers([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // ── Auto-fill email + auto-load invoices when customer changes ────────────────
  useEffect(() => {
    if (!selectedCustomer) return;
    // Switching to single-client mode clears multi-client state
    setClientGroups([]);
    setMultiSelectedIds({});
    setMultiGroupsLoaded(false);
    const ap = selectedCustomer.customerCompanyEmail || selectedCustomer.customerEmail || "";
    setEmailTo(ap);
    setInvoices([]);
    setSelectedIds([]);
    setInvoicesLoaded(false);
    loadInvoicesForCustomer(selectedCustomer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  // ── Auto-fill subject/message when date or customer changes ──────────────────
  useEffect(() => {
    if (!selectedCustomer || !selectedDate) return;
    const school = fmtCustomerName(selectedCustomer);
    const formattedDate = dayjs(selectedDate).format("MMM D, YYYY");
    const formattedDateLong = dayjs(selectedDate).format("MMMM D, YYYY");

    const tpl = (apTemplateData as any)?.data;

    if (tpl?.subject) {
      setEmailSubject(
        tpl.subject
          .replace(/\[customer_name\]/g, school)
          .replace(/\[trip_date\]/g, formattedDate)
      );
    } else {
      setEmailSubject(`Charter Invoice(s) – ${school} – ${formattedDate}`);
    }

    if (tpl?.message) {
      const stripped = tpl.message
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s{2,}/g, "\n")
        .trim();
      setEmailMessage(
        stripped
          .replace(/\[customer_name\]/g, school)
          .replace(/\[trip_date\]/g, formattedDateLong)
      );
    } else {
      setEmailMessage(
        `Please find below the invoice details for charter services provided on ${formattedDateLong}.\n\nPayment is due within 30 days of invoice date.\n\nThank you for your business.`
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, selectedDate, apTemplateData]);

  // ── Manual refresh invoices ───────────────────────────────────────────────────
  const handleLoadInvoices = () => loadInvoicesForCustomer(selectedCustomer);

  // ── Select / deselect ─────────────────────────────────────────────────────────
  const toggleSelect = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelectedIds(allChecked ? [] : displayedInvoices.map((i) => i.id));

  // ── Send AP Email ─────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!selectedCustomer) return showErrorMessage("Please select a client.");
    if (selectedIds.length === 0) return showErrorMessage("Select at least one invoice.");
    if (!emailTo.trim()) return showErrorMessage("Recipient email (To) is required.");
    if (!emailSubject.trim()) return showErrorMessage("Subject is required.");
    if (!emailMessage.trim()) return showErrorMessage("Message body is required.");

    try {
      await sendApEmail({
        customer_id: selectedCustomer.id,
        invoice_ids: selectedIds,
        to: emailTo.trim(),
        cc: emailCc.trim() || undefined,
        subject: emailSubject.trim(),
        message: emailMessage.trim(),
        trip_date: selectedDate,
      }).unwrap();

      showMessage("AP email sent successfully!");

      // Refresh invoice list to show updated AP status
      await handleLoadInvoices();

      // Clear selections
      setSelectedIds([]);
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to send AP email.");
    }
  };

  // ── Multi-client select helpers ───────────────────────────────────────────────
  const toggleMultiSelect = (customerId: number, invoiceId: number) => {
    setMultiSelectedIds((prev) => {
      const cur = prev[customerId] ?? [];
      return { ...prev, [customerId]: cur.includes(invoiceId) ? cur.filter((x) => x !== invoiceId) : [...cur, invoiceId] };
    });
  };

  const toggleGroupAll = (customerId: number, filteredInvs: IApInvoice[]) => {
    setMultiSelectedIds((prev) => {
      const cur = prev[customerId] ?? [];
      const allChecked = filteredInvs.length > 0 && filteredInvs.every((i) => cur.includes(i.id));
      return { ...prev, [customerId]: allChecked ? [] : filteredInvs.map((i) => i.id) };
    });
  };

  // ── Batch send: one email per client that has selections ──────────────────────
  const handleSendAll = async () => {
    if (!emailSubject.trim()) return showErrorMessage("Subject is required.");
    if (!emailMessage.trim()) return showErrorMessage("Message body is required.");

    const clientsToSend = clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0);
    if (clientsToSend.length === 0) return showErrorMessage("Select at least one invoice.");

    const missing = clientsToSend.filter((g) => !g.emailTo.trim());
    if (missing.length > 0) {
      return showErrorMessage(`Missing email address for: ${missing.map((g) => fmtCustomerName(g.customer)).join(", ")}`);
    }

    let successCount = 0;
    let failCount = 0;
    const formattedDate    = dayjs(selectedDate).format("MMM D, YYYY");
    const formattedDateLong = dayjs(selectedDate).format("MMMM D, YYYY");

    for (const group of clientsToSend) {
      const school = fmtCustomerName(group.customer);
      const subject = emailSubject.replace(/\[client_name\]/g, school).replace(/\[customer_name\]/g, school).replace(/\[trip_date\]/g, formattedDate);
      const message = emailMessage.replace(/\[client_name\]/g, school).replace(/\[customer_name\]/g, school).replace(/\[trip_date\]/g, formattedDateLong);
      try {
        await sendApEmail({
          customer_id: group.customer.id,
          invoice_ids: multiSelectedIds[group.customer.id],
          to: group.emailTo.trim(),
          cc: emailCc.trim() || undefined,
          subject,
          message,
          trip_date: selectedDate,
        }).unwrap();
        successCount++;
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) showMessage(`${successCount} AP email${successCount > 1 ? "s" : ""} sent successfully!`);
    if (failCount > 0) showErrorMessage(`${failCount} email${failCount > 1 ? "s" : ""} failed to send.`);

    // Reload all clients to refresh AP status
    await loadAllClients(customers);
  };

  // ── Load History ──────────────────────────────────────────────────────────────
  const handleOpenHistory = async () => {
    if (!selectedCustomer) return showErrorMessage("Please select a client first.");
    try {
      const res = await fetchApHistory({ customer_id: selectedCustomer.id }).unwrap();
      setHistoryLogs(res.data ?? []);
      setHistoryOpen(true);
    } catch {
      showErrorMessage("Failed to load email history.");
    }
  };

  // ── Mode ──────────────────────────────────────────────────────────────────────
  const isMultiMode = multiGroupsLoaded && !selectedCustomer;

  // ── Stats ─────────────────────────────────────────────────────────────────────
  // Single-client stats
  const alreadySentCount = invoices.filter((i) => i.ap_email_sent).length;
  const apPendingCount   = invoices.filter((i) => !i.ap_email_sent).length;

  // Multi-client stats
  const allMultiInvoices   = clientGroups.flatMap((g) => g.invoices);
  const multiTotalPending  = allMultiInvoices.filter((i) => !i.ap_email_sent).length;
  const multiClientsPending = clientGroups.filter((g) => g.invoices.some((i) => !i.ap_email_sent)).length;
  const allMultiSelectedIds = Object.values(multiSelectedIds).flat();
  const multiTotalSelected  = allMultiSelectedIds.length;
  const multiTotalAmount    = clientGroups.reduce((sum, g) => {
    const sel = multiSelectedIds[g.customer.id] ?? [];
    return sum + g.invoices.filter((i) => sel.includes(i.id)).reduce((s, i) => s + parseFloat(i.quoteWorkOrderSaleGrandTotal ?? "0"), 0);
  }, 0);
  const multiAlreadySentCount = allMultiInvoices.filter((i) => i.ap_email_sent).length;

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Page Header ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg,#2f3891 0%,#2f3891 100%)",
          borderRadius: 3,
          px: 3,
          py: 2.5,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AttachEmailOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
              AP Email Sender
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12, mt: 0.4 }}>
              Send consolidated accounts payable invoices to client schools
            </Typography>
          </Box>
        </Box>
        {selectedCustomer && (
          <Button
            variant="outlined"
            size="small"
            startIcon={historyLoading ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : <HistoryIcon />}
            onClick={handleOpenHistory}
            sx={{
              color: "#fff",
              borderColor: "rgba(255,255,255,0.4)",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12,
              "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            View History
          </Button>
        )}
      </Box>

      <Grid container spacing={2.5}>

        {/* ── LEFT COLUMN ── */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ── Section 1: Select Date & Client ── */}
            <SectionCard
              icon={<PeopleOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />}
              title="Select Trip Date & Client"
              subtitle="Pick a date first — only clients with trips on that day will appear"
            >
              <Grid container spacing={2} alignItems="flex-start">

                {/* Date — shown first */}
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>
                    Trip Date <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={selectedDate ? dayjs(selectedDate) : null}
                      onChange={(newVal) => {
                        // fires on every keystroke in the text input — only commit complete dates
                        if (newVal && newVal.isValid() && newVal.year() > 1900) {
                          setSelectedDate(newVal.format("YYYY-MM-DD"));
                        }
                      }}
                      onAccept={(newVal) => {
                        // fires when user picks a date from the calendar grid
                        if (newVal && newVal.isValid()) {
                          setSelectedDate(newVal.format("YYYY-MM-DD"));
                        }
                      }}
                      slots={{
                        day: (props: PickersDayProps<any>) => {
                          const isToday = props.day.isSame(dayjs(), "day");
                          const isSelected = props.selected;
                          return (
                            <PickersDay
                              {...props}
                              sx={{
                                borderRadius: "50%",
                                fontSize: 13,
                                fontWeight: isSelected ? 700 : 400,
                                transition: "all 0.18s ease",
                                ...(isToday && !isSelected && {
                                  border: "2px solid #2f3891",
                                  color: "#2f3891",
                                  fontWeight: 700,
                                  bgcolor: "transparent",
                                }),
                                ...(isSelected && {
                                  bgcolor: "#2f3891 !important",
                                  color: "#fff !important",
                                  boxShadow: "0 2px 8px rgba(47,56,145,0.35)",
                                  "&:hover": { bgcolor: "#252e7a !important" },
                                }),
                                ...(!isSelected && {
                                  "&:hover": {
                                    bgcolor: "#eef2ff",
                                    color: "#2f3891",
                                    transform: "scale(1.08)",
                                  },
                                }),
                              }}
                            />
                          );
                        },
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          placeholder: "Select trip date",
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              fontSize: 13,
                              bgcolor: "#fff",
                              fontWeight: 500,
                              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                              "& fieldset": { borderColor: "#e2e8f0" },
                              "&:hover fieldset": { borderColor: "#a5b4fc" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#2f3891",
                                borderWidth: 1.5,
                              },
                              "&.Mui-focused": {
                                boxShadow: "0 0 0 3px rgba(47,56,145,0.12)",
                              },
                            },
                            "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                              fontSize: 18,
                              color: "#2f3891",
                            },
                          },
                        },
                        popper: {
                          sx: {
                            "& .MuiPaper-root": {
                              borderRadius: "16px",
                              boxShadow: "0 20px 60px rgba(15,23,42,0.15), 0 4px 16px rgba(15,23,42,0.08)",
                              border: "1px solid #e2e8f0",
                              overflow: "hidden",
                              animation: "fadeScaleIn 0.18s ease",
                              "@keyframes fadeScaleIn": {
                                from: { opacity: 0, transform: "scale(0.96) translateY(-6px)" },
                                to:   { opacity: 1, transform: "scale(1) translateY(0)" },
                              },
                            },
                            "& .MuiPickersCalendarHeader-root": {
                              px: 2.5,
                              py: 1.25,
                              borderBottom: "1px solid #f1f5f9",
                            },
                            "& .MuiPickersCalendarHeader-label": {
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#1e293b",
                              letterSpacing: "-0.01em",
                            },
                            "& .MuiPickersArrowSwitcher-button": {
                              borderRadius: "8px",
                              transition: "background 0.15s",
                              color: "#64748b",
                              "&:hover": { bgcolor: "#f1f5f9", color: "#2f3891" },
                            },
                            "& .MuiDayCalendar-weekDayLabel": {
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#94a3b8",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            },
                            "& .MuiDayCalendar-slideTransition": {
                              minHeight: 220,
                            },
                            "& .MuiPickersDay-root": {
                              width: 34,
                              height: 34,
                            },
                            "& .MuiPickersCalendarHeader-switchViewButton": {
                              borderRadius: "8px",
                              color: "#2f3891",
                              "&:hover": { bgcolor: "#eef2ff" },
                            },
                            "& .MuiPickersYear-yearButton": {
                              borderRadius: "8px",
                              fontSize: 13,
                              fontWeight: 500,
                              "&.Mui-selected": {
                                bgcolor: "#2f3891 !important",
                                color: "#fff",
                                fontWeight: 700,
                                boxShadow: "0 2px 8px rgba(47,56,145,0.35)",
                              },
                              "&:hover:not(.Mui-selected)": { bgcolor: "#eef2ff", color: "#2f3891" },
                            },
                            "& .MuiPickersMonth-monthButton": {
                              borderRadius: "8px",
                              fontSize: 13,
                              "&.Mui-selected": {
                                bgcolor: "#2f3891 !important",
                                color: "#fff",
                                fontWeight: 700,
                              },
                              "&:hover:not(.Mui-selected)": { bgcolor: "#eef2ff", color: "#2f3891" },
                            },
                          },
                        },
                        actionBar: {
                          actions: ["today", "clear"],
                          sx: {
                            borderTop: "1px solid #f1f5f9",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            "& .MuiButton-root": {
                              borderRadius: "8px",
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: "none",
                              px: 2,
                              py: 0.6,
                              transition: "all 0.15s ease",
                            },
                            "& .MuiButton-root:first-of-type": {
                              bgcolor: "#2f3891",
                              color: "#fff",
                              "&:hover": { bgcolor: "#252e7a", boxShadow: "0 2px 8px rgba(47,56,145,0.35)" },
                            },
                            "& .MuiButton-root:last-of-type": {
                              border: "1px solid #e2e8f0",
                              color: "#64748b",
                              "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Client — auto-filtered to those with trips on the selected date */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                      Client (School) <span style={{ color: "#ef4444" }}>*</span>
                      {customersLoading && (
                        <CircularProgress size={10} sx={{ ml: 0.75, color: "#2f3891", verticalAlign: "middle" }} />
                      )}
                    </Typography>
                    {!customersLoading && customers.length > 0 && (
                      <Chip
                        label={`${customers.length} client${customers.length !== 1 ? "s" : ""} with trips on this date`}
                        size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#ede9fe", color: "#252e7a", border: "1px solid #ddd6fe" }}
                      />
                    )}
                    {!customersLoading && customers.length === 0 && selectedDate && (
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>No trips on this date</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Autocomplete
                      fullWidth
                      options={customers}
                      getOptionLabel={(c: any) => fmtCustomerName(c)}
                      isOptionEqualToValue={(opt: any, val: any) => opt?.id === val?.id}
                      filterOptions={(opts, { inputValue }) => {
                        const q = inputValue.trim().toLowerCase();
                        if (!q) return opts;
                        return opts.filter((c: any) =>
                          fmtCustomerName(c).toLowerCase().includes(q)
                        );
                      }}
                      value={selectedCustomer}
                      onChange={(_, v) => setSelectedCustomer(v)}
                      size="small"
                      loading={customersLoading}
                      noOptionsText={
                        customersLoading
                          ? "Loading clients…"
                          : !selectedDate
                          ? "Select a date first"
                          : "No clients with trips on this date"
                      }
                      renderOption={(props, option: any) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            fontSize: 13,
                            fontWeight: 500,
                            py: "8px !important",
                            px: "14px !important",
                            borderRadius: "8px",
                            mx: "4px",
                            "&.MuiAutocomplete-option[aria-selected='true']": {
                              bgcolor: "#eef2ff",
                              color: "#2f3891",
                              fontWeight: 700,
                            },
                            "&.MuiAutocomplete-option.Mui-focused": {
                              bgcolor: "#f5f7ff",
                            },
                          }}
                        >
                          {fmtCustomerName(option)}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            customersLoading
                              ? "Loading clients…"
                              : !selectedDate
                              ? "Select a date first…"
                              : customers.length === 0
                              ? "No clients found for this date"
                              : "Search or select client…"
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {customersLoading && (
                                  <CircularProgress size={14} sx={{ color: "#2f3891", mr: 0.5 }} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              fontSize: 13,
                              bgcolor: "#fff",
                              fontWeight: 500,
                              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                              "& fieldset": { borderColor: "#e2e8f0" },
                              "&:hover fieldset": { borderColor: "#a5b4fc" },
                              "&.Mui-focused fieldset": {
                                borderColor: "#2f3891",
                                borderWidth: 1.5,
                              },
                              "&.Mui-focused": {
                                boxShadow: "0 0 0 3px rgba(47,56,145,0.12)",
                              },
                            },
                          }}
                        />
                      )}
                      ListboxProps={{
                        sx: {
                          fontSize: 13,
                          py: 0.5,
                          "& .MuiAutocomplete-option": { borderRadius: "8px" },
                        },
                      }}
                      componentsProps={{
                        paper: {
                          sx: {
                            borderRadius: "12px",
                            boxShadow: "0 12px 40px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)",
                            border: "1px solid #e2e8f0",
                            mt: 0.5,
                            overflow: "hidden",
                          },
                        },
                      }}
                    />
                    <Tooltip title="Refresh invoices" arrow>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleLoadInvoices}
                          disabled={!selectedCustomer || invoicesLoading}
                          sx={{ bgcolor: "#ede9fe", color: "#2f3891", borderRadius: 1.5, width: 36, height: 36, flexShrink: 0, "&:hover": { bgcolor: "#ddd6fe" }, "&:disabled": { bgcolor: "#f1f5f9", color: "#cbd5e1" } }}
                        >
                          {invoicesLoading ? <CircularProgress size={14} sx={{ color: "#2f3891" }} /> : <RefreshIcon sx={{ fontSize: 17 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  {!customersLoading && customers.length === 0 && selectedDate && (
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                      No trips found on {dayjs(selectedDate).format("MMM D, YYYY")}. Try a different date.
                    </Typography>
                  )}
                </Grid>

                {/* ── Load All Clients row ── */}
                {customers.length > 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        bgcolor: isMultiMode ? "#eef2ff" : "#f8fafc",
                        border: `1px solid ${isMultiMode ? "#c7d2fe" : "#e2e8f0"}`,
                        borderRadius: 2,
                        px: 2,
                        py: 1.25,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: isMultiMode ? "#2f3891" : "#475569" }}>
                          {isMultiMode
                            ? `Multi-client mode active — ${clientGroups.length} client${clientGroups.length !== 1 ? "s" : ""} loaded`
                            : customers.length === 1
                              ? `1 client has trips on this date`
                              : `${customers.length} clients have trips on this date`}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                          {isMultiMode
                            ? "Invoices grouped by client below. Select a specific client above to return to single-client mode."
                            : customers.length === 1
                              ? "Load this client's invoices for a grouped view."
                              : "Load all clients at once to handle multiple AP emails in one session."}
                        </Typography>
                      </Box>
                      <Button
                        variant={isMultiMode ? "outlined" : "contained"}
                        size="small"
                        startIcon={loadingAllClients ? <CircularProgress size={13} sx={{ color: isMultiMode ? "#2f3891" : "#fff" }} /> : <PeopleOutlinedIcon />}
                        onClick={() => loadAllClients(customers)}
                        disabled={loadingAllClients || customersLoading}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          fontSize: 12,
                          borderRadius: 1.5,
                          flexShrink: 0,
                          ...(isMultiMode
                            ? { color: "#2f3891", borderColor: "#c7d2fe", bgcolor: "#fff", "&:hover": { bgcolor: "#eef2ff" } }
                            : { bgcolor: "#2f3891", color: "#fff", "&:hover": { bgcolor: "#252e7a" }, "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }),
                        }}
                      >
                        {loadingAllClients ? "Loading…" : isMultiMode ? "Reload All" : "Load All Clients"}
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </SectionCard>

            {/* ── Section 2: Invoice Table ── */}
            <SectionCard
              icon={<ReceiptLongOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />}
              title={isMultiMode ? "Invoices by Client" : "Select Invoices"}
              subtitle={
                isMultiMode
                  ? `${allMultiInvoices.length} invoice${allMultiInvoices.length !== 1 ? "s" : ""} across ${clientGroups.length} client${clientGroups.length !== 1 ? "s" : ""} — ${dayjs(selectedDate).format("MMM D, YYYY")}`
                  : invoicesLoaded
                    ? `${invoices.length} invoice${invoices.length !== 1 ? "s" : ""} found for ${dayjs(selectedDate).format("MMM D, YYYY")}`
                    : "Select a client to load invoices"
              }
              action={
                isMultiMode ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {multiAlreadySentCount > 0 && (
                      <Chip
                        icon={<CheckCircleOutlinedIcon sx={{ fontSize: 13, color: "#16a34a !important" }} />}
                        label={`${multiAlreadySentCount} sent`}
                        size="small"
                        sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 600, fontSize: 10, border: "1px solid #bbf7d0" }}
                      />
                    )}
                    {multiTotalPending > 0 && (
                      <Chip
                        label={`${multiTotalPending} pending`}
                        size="small"
                        sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 600, fontSize: 10, border: "1px solid #fed7aa" }}
                      />
                    )}
                    <Chip
                      label={`${multiTotalSelected} selected`}
                      size="small"
                      sx={{
                        bgcolor: multiTotalSelected > 0 ? "#ede9fe" : "#f1f5f9",
                        color: multiTotalSelected > 0 ? "#252e7a" : "#94a3b8",
                        fontWeight: 600,
                        fontSize: 10,
                      }}
                    />
                  </Box>
                ) : invoices.length > 0 ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {alreadySentCount > 0 && (
                      <Chip
                        icon={<CheckCircleOutlinedIcon sx={{ fontSize: 13, color: "#16a34a !important" }} />}
                        label={`${alreadySentCount} already sent`}
                        size="small"
                        sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 600, fontSize: 10, border: "1px solid #bbf7d0" }}
                      />
                    )}
                    <Chip
                      label={`${selectedIds.length} selected`}
                      size="small"
                      sx={{
                        bgcolor: selectedIds.length > 0 ? "#ede9fe" : "#f1f5f9",
                        color: selectedIds.length > 0 ? "#252e7a" : "#94a3b8",
                        fontWeight: 600,
                        fontSize: 10,
                      }}
                    />
                  </Box>
                ) : undefined
              }
            >
              {isMultiMode ? null /* multi-mode content rendered below */ : !invoicesLoaded ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <ReceiptLongOutlinedIcon sx={{ fontSize: 40, color: "#e2e8f0", mb: 1.5 }} />
                  <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                    Select a specific client above, or use <strong>Load All Clients</strong> to handle multiple clients.
                  </Typography>
                </Box>
              ) : invoices.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <InfoOutlinedIcon sx={{ fontSize: 40, color: "#fbbf24", mb: 1.5 }} />
                  <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>
                    No invoices found for this date.
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.5 }}>
                    Try a different date or check that the invoices exist.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Filter tabs — single-client mode */}
                  <Box sx={{ mb: 1.5 }}>
                    <FilterTabs
                      filter={invoiceFilter}
                      onChange={setInvoiceFilter}
                      counts={{ all: invoices.length, pending: apPendingCount, sent: alreadySentCount }}
                    />
                  </Box>
                <TableContainer sx={{ borderRadius: 1.5, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell padding="checkbox" sx={{ borderBottom: "1px solid #e2e8f0" }}>
                          <Checkbox
                            size="small"
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={toggleAll}
                            sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#2f3891" }, "&.MuiCheckbox-indeterminate": { color: "#2f3891" } }}
                          />
                        </TableCell>
                        {["Charter #", "Trip Date", "Description", "PO #", "Amount", "Paid", "Status", "AP Sent"].map((h) => (
                          <TableCell
                            key={h}
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#64748b",
                              textTransform: "uppercase",
                              letterSpacing: 0.6,
                              borderBottom: "1px solid #e2e8f0",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedInvoices.map((inv) => {
                        const checked = selectedIds.includes(inv.id);
                        const status = deriveStatus(inv.amount_paid, inv.quoteWorkOrderSaleGrandTotal);
                        const saleArr = Array.isArray(inv.quoteWorkOrderSale)
                          ? inv.quoteWorkOrderSale
                          : (typeof inv.quoteWorkOrderSale === "string"
                              ? (() => { try { return JSON.parse(inv.quoteWorkOrderSale); } catch { return []; } })()
                              : []);
                        const desc = saleArr?.[0]?.productName ?? "Charter Trip";

                        return (
                          <TableRow
                            key={inv.id}
                            onClick={() => toggleSelect(inv.id)}
                            hover
                            sx={{
                              cursor: "pointer",
                              bgcolor: checked ? "#f5f3ff" : "inherit",
                              "& td": { borderBottom: "1px solid #f1f5f9" },
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() => toggleSelect(inv.id)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#2f3891" } }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                                #{inv.InvoiceNo ?? inv.quoteWorkOrderNo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                                {fmtDate(inv.quoteWorkOrderIssueDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 12, color: "#475569", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {desc}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 12, color: "#475569" }}>
                                {inv.po_number ?? "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                                {fmtAmt(inv.quoteWorkOrderSaleGrandTotal)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                                {fmtAmt(inv.amount_paid)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={status.label}
                                size="small"
                                sx={{
                                  bgcolor: status.bg,
                                  color: status.color,
                                  fontWeight: 700,
                                  fontSize: 10,
                                  height: 20,
                                  border: `1px solid ${status.color}33`,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {inv.ap_email_sent ? (
                                <Tooltip title={inv.ap_sent_at ? `Sent ${dayjs(inv.ap_sent_at).format("MMM D, YYYY")}` : "AP email sent"}>
                                  <Chip
                                    icon={<CheckCircleOutlinedIcon sx={{ fontSize: 12, color: "#16a34a !important" }} />}
                                    label="Sent"
                                    size="small"
                                    sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: 10, height: 20, border: "1px solid #bbf7d0" }}
                                  />
                                </Tooltip>
                              ) : (
                                <Chip
                                  label="Pending"
                                  size="small"
                                  sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 700, fontSize: 10, height: 20, border: "1px solid #fed7aa" }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
              )}

              {/* ── Multi-client grouped view ── */}
              {isMultiMode && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Filter tabs — multi-client mode */}
                  <FilterTabs
                    filter={invoiceFilter}
                    onChange={setInvoiceFilter}
                    counts={{ all: allMultiInvoices.length, pending: multiTotalPending, sent: multiAlreadySentCount }}
                  />

                  {clientGroups.map((group) => {
                    const filteredInvs = group.invoices.filter((i) => {
                      if (invoiceFilter === "pending") return !i.ap_email_sent;
                      if (invoiceFilter === "sent") return i.ap_email_sent;
                      return true;
                    });
                    if (filteredInvs.length === 0) return null;

                    const clientSel = multiSelectedIds[group.customer.id] ?? [];
                    const groupAllChecked = filteredInvs.length > 0 && filteredInvs.every((i) => clientSel.includes(i.id));
                    const groupSomeChecked = filteredInvs.some((i) => clientSel.includes(i.id)) && !groupAllChecked;
                    const groupPending = group.invoices.filter((i) => !i.ap_email_sent).length;
                    const groupTotal = group.invoices.reduce((s, i) => s + parseFloat(i.quoteWorkOrderSaleGrandTotal ?? "0"), 0);

                    return (
                      <Box key={group.customer.id}>
                        {/* Client header */}
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 1.5,
                          bgcolor: clientSel.length > 0 ? "#eef2ff" : "#f8fafc",
                          border: `1px solid ${clientSel.length > 0 ? "#c7d2fe" : "#e2e8f0"}`,
                          borderRadius: "8px 8px 0 0",
                          px: 1.5, py: 1,
                        }}>
                          <Checkbox
                            size="small"
                            checked={groupAllChecked}
                            indeterminate={groupSomeChecked}
                            onChange={() => toggleGroupAll(group.customer.id, filteredInvs)}
                            sx={{ p: 0.25, color: "#cbd5e1", "&.Mui-checked": { color: "#2f3891" }, "&.MuiCheckbox-indeterminate": { color: "#2f3891" } }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>
                              {fmtCustomerName(group.customer)}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                              {group.invoices.length} invoice{group.invoices.length !== 1 ? "s" : ""} · {fmtAmt(groupTotal)}
                            </Typography>
                          </Box>
                          {/* Per-client email To field */}
                          <TextField
                            size="small"
                            placeholder="AP email address"
                            value={group.emailTo}
                            onChange={(e) => {
                              const updated = clientGroups.map((g) =>
                                g.customer.id === group.customer.id ? { ...g, emailTo: e.target.value } : g
                              );
                              setClientGroups(updated);
                            }}
                            sx={{
                              width: 220,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1.5,
                                fontSize: 12,
                                bgcolor: "#fff",
                                "& fieldset": { borderColor: group.emailTo ? "#e2e8f0" : "#fca5a5" },
                              },
                            }}
                          />
                          {groupPending > 0 && (
                            <Chip label={`${groupPending} pending`} size="small"
                              sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 700, fontSize: 10, height: 20, border: "1px solid #fed7aa", flexShrink: 0 }} />
                          )}
                          {clientSel.length > 0 && (
                            <Chip label={`${clientSel.length} sel`} size="small"
                              sx={{ bgcolor: "#ede9fe", color: "#252e7a", fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0 }} />
                          )}
                        </Box>

                        {/* Invoice rows */}
                        <TableContainer sx={{ borderRadius: "0 0 8px 8px", border: "1px solid #e2e8f0", borderTop: "none", overflow: "hidden" }}>
                          <Table size="small">
                            <TableBody>
                              {filteredInvs.map((inv) => {
                                const checked = clientSel.includes(inv.id);
                                const status = deriveStatus(inv.amount_paid, inv.quoteWorkOrderSaleGrandTotal);
                                const saleArr = Array.isArray(inv.quoteWorkOrderSale)
                                  ? inv.quoteWorkOrderSale
                                  : (typeof inv.quoteWorkOrderSale === "string"
                                      ? (() => { try { return JSON.parse(inv.quoteWorkOrderSale); } catch { return []; } })()
                                      : []);
                                const desc = saleArr?.[0]?.productName ?? "Charter Trip";
                                return (
                                  <TableRow key={inv.id} onClick={() => toggleMultiSelect(group.customer.id, inv.id)} hover
                                    sx={{ cursor: "pointer", bgcolor: checked ? "#f5f3ff" : "inherit", "& td": { borderBottom: "1px solid #f1f5f9" } }}>
                                    <TableCell padding="checkbox">
                                      <Checkbox size="small" checked={checked}
                                        onChange={() => toggleMultiSelect(group.customer.id, inv.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#2f3891" } }} />
                                    </TableCell>
                                    <TableCell><Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>#{inv.InvoiceNo ?? inv.quoteWorkOrderNo}</Typography></TableCell>
                                    <TableCell><Typography sx={{ fontSize: 12, color: "#475569", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{desc}</Typography></TableCell>
                                    <TableCell><Typography sx={{ fontSize: 12, color: "#475569" }}>{inv.po_number ?? "—"}</Typography></TableCell>
                                    <TableCell><Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>{fmtAmt(inv.quoteWorkOrderSaleGrandTotal)}</Typography></TableCell>
                                    <TableCell>
                                      <Chip label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 10, height: 20, border: `1px solid ${status.color}33` }} />
                                    </TableCell>
                                    <TableCell>
                                      {inv.ap_email_sent ? (
                                        <Tooltip title={inv.ap_sent_at ? `Sent ${dayjs(inv.ap_sent_at).format("MMM D, YYYY")}` : "AP email sent"}>
                                          <Chip icon={<CheckCircleOutlinedIcon sx={{ fontSize: 12, color: "#16a34a !important" }} />} label="Sent" size="small"
                                            sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: 10, height: 20, border: "1px solid #bbf7d0" }} />
                                        </Tooltip>
                                      ) : (
                                        <Chip label="Pending" size="small"
                                          sx={{ bgcolor: "#fff7ed", color: "#c2410c", fontWeight: 700, fontSize: 10, height: 20, border: "1px solid #fed7aa" }} />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </SectionCard>

          </Box>
        </Grid>

        {/* ── RIGHT COLUMN ── */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, position: { lg: "sticky" }, top: { lg: 24 } }}>

            {/* ── Selection Summary ── */}
            <SectionCard
              icon={<ReceiptLongOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />}
              title="Selection Summary"
            >
              {isMultiMode ? (
                /* ── Multi-client summary ── */
                <Box>
                  {/* Top stat badges — clicking filters the invoice table */}
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                    <StatBadge
                      label="Selected"
                      value={multiTotalSelected}
                      active={invoiceFilter === "all"}
                      onClick={() => setInvoiceFilter("all")}
                    />
                    <StatBadge
                      label="Total $"
                      value={`$${multiTotalAmount.toFixed(2)}`}
                      color="#10b981"
                      active={invoiceFilter === "sent"}
                      onClick={() => setInvoiceFilter(invoiceFilter === "sent" ? "all" : "sent")}
                    />
                    <Tooltip title={invoiceFilter === "pending" ? "Click to show all" : `Click to show only pending invoices`} arrow>
                      <span>
                        <StatBadge
                          label={`Pending (${multiClientsPending} client${multiClientsPending !== 1 ? "s" : ""})`}
                          value={multiTotalPending}
                          color="#d97706"
                          active={invoiceFilter === "pending"}
                          onClick={() => setInvoiceFilter(invoiceFilter === "pending" ? "all" : "pending")}
                        />
                      </span>
                    </Tooltip>
                  </Box>

                  {/* Per-client email breakdown */}
                  {multiTotalSelected === 0 ? (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", textAlign: "center", py: 1 }}>
                      Select invoices from any client group
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {/* Label */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                        <SendIcon sx={{ fontSize: 13, color: "#64748b" }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).length} separate email{clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).length !== 1 ? "s" : ""} will be sent
                        </Typography>
                      </Box>

                      {clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).map((group, idx) => {
                        const sel = multiSelectedIds[group.customer.id] ?? [];
                        const selInvs = group.invoices.filter((i) => sel.includes(i.id));
                        const selAmt = selInvs.reduce((s, i) => s + parseFloat(i.quoteWorkOrderSaleGrandTotal ?? "0"), 0);
                        const clientsToSendCount = clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).length;
                        return (
                          <Box key={group.customer.id} sx={{
                            bgcolor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 1,
                            position: "relative",
                          }}>
                            {/* Email number indicator */}
                            <Box sx={{
                              position: "absolute", top: -8, left: 10,
                              bgcolor: "#2f3891", color: "#fff",
                              fontSize: 9, fontWeight: 700,
                              borderRadius: 1, px: 0.75, py: 0.2,
                              lineHeight: 1.4,
                            }}>
                              Email {idx + 1} of {clientsToSendCount}
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.4 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
                                {fmtCustomerName(group.customer)}
                              </Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#2f3891" }}>{fmtAmt(selAmt)}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                              {sel.length} invoice{sel.length !== 1 ? "s" : ""}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: group.emailTo ? "#475569" : "#ef4444", mt: 0.2 }}>
                              → {group.emailTo || "⚠ No email address set"}
                            </Typography>
                          </Box>
                        );
                      })}
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                          Grand Total
                        </Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#2f3891" }}>
                          ${multiTotalAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                /* ── Single-client summary ── */
                <Box>
                  {invoicesLoaded && invoices.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                      <StatBadge
                        label="Selected"
                        value={selectedIds.length}
                        active={invoiceFilter === "all"}
                        onClick={() => setInvoiceFilter("all")}
                      />
                      <StatBadge
                        label="Total $"
                        value={`$${totalSelected.toFixed(2)}`}
                        color="#10b981"
                        active={invoiceFilter === "sent"}
                        onClick={() => setInvoiceFilter(invoiceFilter === "sent" ? "all" : "sent")}
                      />
                      <Tooltip title={invoiceFilter === "pending" ? "Click to show all invoices" : "Click to show only pending invoices"} arrow>
                        <span>
                          <StatBadge
                            label="AP Pending"
                            value={apPendingCount}
                            color="#d97706"
                            active={invoiceFilter === "pending"}
                            onClick={() => setInvoiceFilter(invoiceFilter === "pending" ? "all" : "pending")}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                  {selectedIds.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", textAlign: "center", py: 1 }}>
                      No invoices selected
                    </Typography>
                  ) : (
                    <Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {selectedInvoices.map((inv) => (
                          <Box key={inv.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1.5, px: 1.5, py: 0.75 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                              #{inv.InvoiceNo ?? inv.quoteWorkOrderNo}
                            </Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>
                              {fmtAmt(inv.quoteWorkOrderSaleGrandTotal)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Total</Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#2f3891" }}>
                          ${totalSelected.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </SectionCard>

            {/* ── Compose Email ── */}
            <SectionCard
              icon={<EmailOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />}
              title="Compose AP Email"
              subtitle={
                isMultiMode
                  ? multiTotalSelected === 0
                    ? "Select invoices to enable"
                    : `Will send to ${clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).length} client${clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).length !== 1 ? "s" : ""}`
                  : selectedIds.length === 0
                    ? "Select invoices to enable"
                    : `${selectedIds.length} invoice${selectedIds.length !== 1 ? "s" : ""} will be included`
              }
            >
              {(() => {
                const disabled = isMultiMode ? multiTotalSelected === 0 : selectedIds.length === 0;
                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : undefined }}>

                    {/* "To" field — only shown in single-client mode */}
                    {!isMultiMode && (
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>
                          To <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="ap@schoolboard.ca"
                          value={emailTo}
                          onChange={(e) => setEmailTo(e.target.value)}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
                        />
                      </Box>
                    )}

                    {/* Multi-mode: per-client recipients hint */}
                    {isMultiMode && (
                      <Box sx={{ bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5, px: 1.5, py: 1 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#15803d", mb: 0.25 }}>
                          Recipients set per client
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                          Each client's AP email is configured in their group header above. One email will be sent per client.
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>
                        CC{" "}
                        <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>
                          (optional, shared)
                        </Box>
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="finance@company.com"
                        value={emailCc}
                        onChange={(e) => setEmailCc(e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
                      />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>
                        Subject <span style={{ color: "#ef4444" }}>*</span>
                        {isMultiMode && (
                          <Box component="span" sx={{ fontSize: 10, fontWeight: 400, color: "#94a3b8", ml: 0.75 }}>
                            — use [client_name] for personalization
                          </Box>
                        )}
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
                      />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>
                        Message <span style={{ color: "#ef4444" }}>*</span>
                        {isMultiMode && (
                          <Box component="span" sx={{ fontSize: 10, fontWeight: 400, color: "#94a3b8", ml: 0.75 }}>
                            — use [client_name] for personalization
                          </Box>
                        )}
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={5}
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        placeholder="Message to the AP department…"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, bgcolor: "#fff" } }}
                      />
                      <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                        Invoice details table will be appended automatically.
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={sendingEmail ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SendIcon />}
                      onClick={isMultiMode ? handleSendAll : handleSend}
                      disabled={sendingEmail || disabled}
                      sx={{
                        background: "linear-gradient(135deg,#2f3891,#2f3891)",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 14,
                        borderRadius: 2,
                        py: 1.25,
                        boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
                        "&:hover": { background: "linear-gradient(135deg,#252e7a,#252e7a)" },
                        "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
                      }}
                    >
                      {sendingEmail
                        ? "Sending…"
                        : isMultiMode
                          ? `Send to ${clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).length} Client${clientGroups.filter((g) => (multiSelectedIds[g.customer.id] ?? []).length > 0).length !== 1 ? "s" : ""} (${multiTotalSelected} invoices)`
                          : `Send AP Email${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
                    </Button>
                  </Box>
                );
              })()}
            </SectionCard>

          </Box>
        </Grid>
      </Grid>

      {/* ── History Dialog ── */}
      <HistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        logs={historyLogs}
      />

    </Box>
  );
};

export default APEmailPage;
